import { useParams, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { categories, siteConfig } from "@/mocks/siteData";
import { categoryArticles } from "@/mocks/categoryData";
import PageMeta from "@/components/feature/PageMeta";
import VideoCard from "@/components/base/VideoCard";
import Breadcrumb from "@/components/feature/Breadcrumb";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || "dating-tips");

  useEffect(() => {
    if (slug) setActiveCategory(slug);
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);
  const articles = categoryArticles[activeCategory] || [];

  const breadcrumbItems = [
    { label: "首页", href: "/" },
    { label: currentCategory?.name || "分类" },
  ];

  const categorySchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${currentCategory?.name || ""} - ${siteConfig.siteName}`,
    description: currentCategory?.description || "",
    url: `${siteConfig.siteUrl}/category/${activeCategory}`,
    isPartOf: {
      "@type": "WebSite",
      name: siteConfig.siteName,
      url: siteConfig.siteUrl,
    },
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: siteConfig.siteUrl },
        { "@type": "ListItem", position: 2, name: currentCategory?.name || "", item: `${siteConfig.siteUrl}/category/${activeCategory}` },
      ],
    },
  };

  return (
    <>
      <PageMeta
        title={`${currentCategory?.name || "分类"} - ${siteConfig.siteName}在线观看`}
        description={currentCategory?.description || siteConfig.siteDescription}
        keywords={`${currentCategory?.name},${siteConfig.siteKeywords}`}
        canonicalUrl={`${siteConfig.siteUrl}/category/${activeCategory}`}
        ogTitle={`${currentCategory?.name || "分类"} - ${siteConfig.siteName}在线观看`}
        ogDescription={currentCategory?.description || siteConfig.siteDescription}
        ogType="website"
        ogUrl={`${siteConfig.siteUrl}/category/${activeCategory}`}
        jsonLd={[categorySchema]}
      />

      <main className="min-h-screen bg-background-50 pt-16">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-8">
          <div className="mb-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
              {currentCategory?.name}
            </h1>
            <p className="text-sm text-foreground-500">{currentCategory?.description}</p>
          </div>

          <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 text-sm rounded-full whitespace-nowrap transition-all cursor-pointer ${
                  activeCategory === cat.slug
                    ? "bg-primary-500 text-background-50 font-medium"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                <i className={`${cat.icon} mr-1.5`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {articles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {articles.map((article) => (
                <VideoCard
                  key={article.id}
                  id={article.id}
                  title={article.title}
                  category={activeCategory}
                  categoryName={currentCategory?.name || ""}
                  thumbnail={article.thumbnail}
                  duration={article.duration}
                  views={article.views}
                  date={article.date}
                  author={article.author}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-video-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">暂无内容，敬请期待</p>
            </div>
          )}
        </div>
      </main>
    </>
  );
}